import { useEffect, useReducer, useState } from "react";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n";
import { getStoredLang, isEngineRoute, publicLangOf, resolveLanguage, supportedLang } from "@/i18n/languages";
import { ENGINE_NS, SHELL_NS, hasCatalogs, loadCatalogs } from "@/i18n/lazyCatalogs";
import { canUseAppLang } from "@/lib/publicLocales";

const NONE = [];

// Orden de D2 (spec i18n global). El Engine conserva resolveLanguage tal cual; fuera de el y de las rutas con
// prefijo, todo pasa por canUseAppLang: con la compuerta `app` cerrada y sin `lang_preview` sale es. Con sesion
// no se mira el navegador: un usuario de hoy con Chrome en ingles no puede ver cambiar su app.
// Vive aqui y no en languages.js porque este lo importa i18n/index.js: arrastraria las compuertas al arranque.
export function resolveAppLanguage({ pathname, user, member, orgLanguage, navigatorLanguages, stored, query } = {}) {
  const fromPrefix = publicLangOf(pathname);
  if (fromPrefix) return fromPrefix;
  if (isEngineRoute(pathname)) return resolveLanguage({ member, user, orgLanguage, navigatorLanguages });
  const lang = user
    ? supportedLang(user.language) || "es"
    : supportedLang(query) || supportedLang(stored) || "es";
  return canUseAppLang(lang, user) ? lang : "es";
}

// En el Engine espera a su catalogo; fuera, solo si el idioma no es es (hoy solo con la compuerta `app` o
// `lang_preview`). Las rutas con prefijo publico traen sus catalogos con la pagina (withCatalogs).
const catalogsFor = (pathname, lang) => {
  if (isEngineRoute(pathname)) return ENGINE_NS;
  if (lang === "es" || publicLangOf(pathname)) return NONE;
  return SHELL_NS;
};

const queryLang = (search) => {
  try {
    return new URLSearchParams(search || "").get("lang");
  } catch {
    return null;
  }
};

// El idioma lo decide resolveAppLanguage (orden de D2). Al desmontarse (se sale de /app) se vuelve a es, porque
// ahi no hay otro punto que lo haga.
// Devuelve si el idioma ya esta en su sitio: en el Engine, tras contestar /engine/me (`engineLoading`) y con el
// catalogo de ese idioma cargado; fuera, con es siempre, y con otro idioma cuando esta su catalogo `shell`.
// Hasta entonces AppLayout no pinta la pagina (si no, salia en es un instante).
// Si el catalogo no llega se queda en es y se da por asentado: mejor en espanol que una pantalla vacia.
export default function useAppLanguage({ member, user, orgLanguage, pathname, engineLoading = false, search }) {
  const { i18n: live } = useTranslation();
  const [failed, setFailed] = useState(null);
  const [, rerender] = useReducer((n) => n + 1, 0);
  const anonymous = !user;
  const wanted = resolveAppLanguage({
    pathname,
    user,
    member,
    orgLanguage,
    navigatorLanguages: navigator.languages,
    stored: anonymous ? getStoredLang() : null,
    query: anonymous ? queryLang(search ?? window.location.search) : null,
  });
  const needs = catalogsFor(pathname, wanted);
  // Incluye los namespaces para que un fallo de en/shell no de por cargado en/engine.
  const failedKey = `${wanted}:${needs.join(",")}`;

  // pathname tambien: el selector cambia el idioma antes de que vuelva el PUT, y salir del
  // Engine en esa ventana no cambia `wanted` (sigue siendo es) pero si tiene que devolverlo a es.
  // Se cambia cuando ya esta el catalogo en ese idioma (i18n/lazyCatalogs.js): antes no. Si el idioma ya era el
  // bueno (del B2C en ingles al Engine en ingles) tambien hay que esperar al catalogo que falte.
  // Con es ya puesto no se espera a nada: asi era antes en el Engine y asi sigue el B2C.
  useEffect(() => {
    const ready = hasCatalogs(needs, wanted);
    if (i18n.language === wanted && (ready || wanted === "es")) return undefined;
    if (ready) {
      i18n.changeLanguage(wanted);
      return undefined;
    }
    let alive = true;
    loadCatalogs(needs, wanted).then(() => {
      if (!alive) return;
      if (i18n.language !== wanted) i18n.changeLanguage(wanted);
      // El idioma ya era el bueno pero el catalogo acaba de llegar: sin repintar, no se daria por asentado.
      else rerender();
    }, () => {
      if (!alive) return;
      if (i18n.language === wanted && wanted !== "es") i18n.changeLanguage("es");
      setFailed(failedKey);
    });
    return () => { alive = false; };
  }, [wanted, pathname, needs]);

  useEffect(() => () => { i18n.changeLanguage("es"); }, []);

  // Fuera del Engine solo decimos asentado cuando el idioma activo ya es es: evita pintar un instante
  // en ingles al volver del Engine. Es/common, es/public y es/shell son estaticos, el cambio es inmediato.
  if (!isEngineRoute(pathname) && wanted === "es") return live.language === "es";
  const ready = live.language === wanted && (wanted === "es" || hasCatalogs(needs, wanted));
  if (!isEngineRoute(pathname)) return ready || failed === failedKey;
  return !engineLoading && (ready || failed === failedKey);
}
